import React, { useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import "./BarChart.css";
import { colors } from "@mui/material";
import { graphDataFunc } from "./projects/users/liveFindings/service";
import { formatCalenderDate } from "./projects/users/web-workflow/utils/util";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import { RiRefreshLine } from "react-icons/ri";

const loadingPlugin = {
  id: "loadingPlugin",
  beforeDraw: (chart) => {
    if (chart.config.options.isLoading) {
      const { ctx, chartArea } = chart;
      ctx.save();
      ctx.font = "16px sans-serif";
      ctx.fillStyle = "#666";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(
        "Loading graph...",
        chartArea.width / 2,
        chartArea.height / 2
      );
      ctx.restore();
    }
  },
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  loadingPlugin,
  Filler
);

const BarChart = ({
  range,
  setRange,
  graphData,
  setGraphData,
  liveFindingsFilter,
  loadingGraph,
  setLoadingGraph,
}) => {
  //  const [chartType, setChartType] = useState("bar"); // ✅ dropdown selection

 const Xlabels =
  graphData?.plot_points?.map((point) => {
    const d = new Date(point.x);

    if (range === "day") {
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }

    if (range === "month") {
      return d.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      });
    }

    if (range === "year") {
      return d.getFullYear().toString();
    }

    return "";
  }) || [];


  const Ylabels = graphData?.plot_points?.map((point) => point?.y) || [];
  // const baseDataset = {
  //   label: `Revenue (${range})`,
  //   data: Ylabels,
  //   fill: true,
  //   backgroundColor: [
  //     "rgba(75, 192, 192, 0.6)",
  //     "rgba(153, 102, 255, 0.6)",
  //     "rgba(255, 159, 64, 0.6)",
  //     "rgba(255, 99, 132, 0.6)",
  //     "rgba(54, 162, 235, 0.6)",
  //   ],
  //   borderColor: "rgba(89, 230, 230, 1)",
  //   tension: 0.4,
  // };

  // const data = {
  //   labels: Xlabels,
  //   datasets: [baseDataset],
  // };

  const data = loadingGraph
    ? { labels: [], datasets: [] }
    : {
        labels: Xlabels,
        datasets: [
          {
            label: `Revenue (${range})`,
            data: Ylabels,
            fill: true,
            backgroundColor: "rgba(75, 192, 192, 0.6)",
            // "rgba(75, 192, 192, 0.2)",
            fontWeight: "bold",
            borderColor: "rgba(89, 230, 230, 1)",
            tension: 0.4,
          },
        ],
      };

const options = {
  responsive: true,
  maintainAspectRatio: false,
  isLoading: loadingGraph,
  plugins: {
    legend: {
      display: true,
      position: "top",
      labels: {
        font: {
          weight: "bold", // ✅ Bold legend text
        },
      },
    },
    title: {
      display: true,
      text: `Total Revenue${
        graphData?.summary?.total_revenue
          ? ` - $${graphData.summary.total_revenue.toLocaleString()}`
          : ""
      }`,
      font: { size: 14, weight: "bold" }, // ✅ Bold title
      color: "#333",
    },
    tooltip: {
      bodyFont: {
        weight: "bold", // ✅ Bold tooltip body
      },
      callbacks: {
        label: (ctx) => `$${ctx.raw.toLocaleString()} on ${ctx.label}`,
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        font: {
          weight: "bold", // ✅ Bold Y-axis numbers
        },
        callback: (value) => `$${value.toLocaleString()}`,
      },
    },
    x: {
      grid: { display: false },
      ticks: {
        font: {
          weight: "bold", // ✅ Bold X-axis labels
        },
        autoSkip: true,
        maxRotation: 45,
        minRotation: 45,
      },
    },
  },
};


  const hanldeGranularityChange = (e) => {
    setLoadingGraph(true);

    const payload = {
      start_date: formatCalenderDate(liveFindingsFilter.start_date),
      end_date: formatCalenderDate(liveFindingsFilter.end_date),
      episode_identifier: liveFindingsFilter.episode_identifier,
    };
    graphDataFunc({ ...payload, granularity: e.target.innerText.toLowerCase() })
      .then((res) => {
        setGraphData(res);
        // setLiveFindingsFilter({
        //   start_date: null,
        //   end_date: null,
        //   episode_identifier: "",
        // });
        setLoadingGraph(false);
      })
      .catch((error) => {
        console.error("Error fetching stats:", error);
        setLoadingGraph(false);
      });

    setRange(e.target.innerText.toLowerCase());
  };

  return (
    <div className="relative w-[600px] h-[400px]">
      {/* ✅ Header + Buttons Row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 12px",
        }}
      >
        <div
          style={{
            fontSize: "16px",
            fontWeight: "bold",
            color: "#333",
          }}
        >
          Live API Dashboard
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          {/* {["day", "month", "year"].map((btn) => (
          <button
            key={btn}
            onClick={hanldeGranularityChange}
            className={`px-3 py-1 rounded border-0 ${
              range === btn
                ? "graph-btn-selected"
                : "graph-btn-selected-not-selected"
            }`}
            style={{
              cursor: "pointer",
              minWidth: "70px",
              textAlign: "center",
            }}
          >
            {btn.charAt(0).toUpperCase() + btn.slice(1)}
          </button>
        ))} */}
         <span
                        className="live-finding-refresh-btn"
                        onClick={() => window.location.reload()}
                      >
                        {/* <strong>Refresh Data</strong> */}
                        <span>
                          {/* <img src={refresh} alt="img" /> */}
                          <RiRefreshLine />
                        </span>
                      </span>
        
          <ButtonGroup aria-label="Basic example">
            {["day", "month", "year"].map((btn) => (
              <Button
                key={btn}
                onClick={hanldeGranularityChange}
                className={`graph-btn ${
                  range === btn
                    ? "graph-btn-selected"
                    : "graph-btn-not-selected"
                }`}
              >
                {btn.charAt(0).toUpperCase() + btn.slice(1)}
              </Button>
            ))}
            {/* <Button variant="secondary">Right</Button> */}
          </ButtonGroup>
        </div>
      </div>

      {/* ✅ Scroll wrapper for day view */}
      <div
        style={{
          overflowX: range === "day" ? "auto" : "hidden",
          width: "100%",
        }}
      >
        <div
  style={{
    width:
      range === "day"
        ? `${Math.max(Xlabels.length * 70, 900)}px` // at least 900px
        : "100%",
    height: "400px",
  }}
>
  <Bar data={data} options={options} />
</div>
      </div>
      <div style={{ textAlign: "right", marginTop: "8px" }}>
  <span style={{ fontSize: "12px", color: "#666" }}>
    Date format: <strong>dd/mm/yyyy</strong>
  </span>
</div>
    </div>
  );
};

export default BarChart;
